import React, { useState, useMemo, useCallback } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Accordion, AccordionTab } from 'primereact/accordion';
import ConceptDetailsSection from 'screens/FDP/components/ConceptDetails';
import NotesSection from 'screens/FDPScreen/components/NotesSection';
import CopyIcon from 'assets/icons/copyIcon.svg';
import ResetIcon from 'assets/icons/revertIcon.svg';
import classes from './salesPlanEdit.module.scss';
import {
  getFutureMonths15,
  mockSalesSummary,
  mockSalesPlanEditData,
  mockConceptDetails,
  mockContractDetails,
  mockNotesData,
  SalesSummaryData,
} from '../salesPlanMockData';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CotOption {
  label: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const makeKey = (cot: string, category: string) => `${cot.toLowerCase()}|${category}`;

const getSalesSummaryValue = (
  summaryData: SalesSummaryData[],
  cotValue: string,
  metric: 'YTD' | '3MA' | 'PM',
  category: 'direct' | 'indirect',
): string => {
  const row = summaryData.find((s) => s.COT.toLowerCase() === cotValue.toLowerCase());
  if (!row) return '-';
  if (metric === 'YTD') {
    const v = category === 'direct' ? row.YearToDateDirectShare : row.YearToDateIndirectShare;
    return v != null ? `${v}%` : '-';
  }
  if (metric === '3MA') {
    const v =
      category === 'direct' ? row.LastThreeMonthDirectShare : row.LastThreeMonthIndirectShare;
    return v != null ? `${v}%` : '-';
  }
  const v = category === 'direct' ? row.LastMonthDirectShare : row.LastMonthIndirectShare;
  return v != null ? `${v}%` : '-';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SalesPlanEdit: React.FC = () => {
  const futureDates = useMemo(() => getFutureMonths15(), []);

  // COT selection — separate for direct and indirect (matches actual useSalesPlanEdit hook)
  const [directCOT, setDirectCOT] = useState<CotOption>({ label: 'CMOP', value: 'cmop' });
  const [indirectCOT, setIndirectCOT] = useState<CotOption>({ label: 'CMOP', value: 'cmop' });

  // Editable adj market forecast values: key = cot|category, subkey = dateString
  const [salesPlanValues, setSalesPlanValues] = useState<Record<string, Record<string, number>>>(
    () => {
      const map: Record<string, Record<string, number>> = {};
      for (const row of mockSalesPlanEditData) {
        const key = makeKey(row.cot, row.category);
        if (!map[key]) map[key] = {};
        map[key][row.planningPeriod] = row.adjMarketForecast;
      }
      return map;
    },
  );

  // copy-row tracking: key = cot+dateString+category
  const [copyRows, setCopyRows] = useState<Set<string>>(new Set());

  // "New values / Old values" InputSwitch state (per cot+isDirect)
  const [oldValuesChecked, setOldValuesChecked] = useState<Record<string, boolean>>({});

  // -------------------------------------------------------------------------
  // Editable value accessors
  // -------------------------------------------------------------------------

  const getAdjMarketForecast = useCallback(
    (cot: string, category: string, dateString: string): number =>
      salesPlanValues[makeKey(cot, category)]?.[dateString] ?? 0,
    [salesPlanValues],
  );

  const getSalesplanValue = useCallback(
    (
      cot: string,
      category: 'direct' | 'indirect',
      dateString: string,
      field: 'marketForecast' | 'adjforecast' | 'tmsvalue' | 'msplan' | 'euforecast',
    ): string => {
      const row = mockSalesPlanEditData.find(
        (r) =>
          r.cot.toLowerCase() === cot.toLowerCase() &&
          r.category === category &&
          r.planningPeriod === dateString,
      );
      if (!row) return '-';
      if (field === 'marketForecast') return row.marketForecast.toLocaleString();
      if (field === 'adjforecast')
        return String(salesPlanValues[makeKey(cot, category)]?.[dateString] ?? row.adjMarketForecast);
      if (field === 'tmsvalue') return row.tmsValue;
      if (field === 'msplan') return row.msPlan;
      if (field === 'euforecast') return row.euSalesPlan.toLocaleString();
      return '-';
    },
    [salesPlanValues],
  );

  const updateForecastValue = useCallback(
    (value: string, dateString: string, cot: string, isDirect: boolean) => {
      const category = isDirect ? 'direct' : 'indirect';
      const num = parseInt(value.replace(/,/g, ''), 10);
      setSalesPlanValues((prev) => ({
        ...prev,
        [makeKey(cot, category)]: {
          ...prev[makeKey(cot, category)],
          [dateString]: Number.isNaN(num) ? 0 : num,
        },
      }));
    },
    [],
  );

  // -------------------------------------------------------------------------
  // Copy / Reset forward
  // -------------------------------------------------------------------------

  const handleCopyButtonClick = useCallback(
    (isDirect: boolean, fromIndex: number) => {
      const cot = isDirect ? directCOT.value : indirectCOT.value;
      const category = isDirect ? 'direct' : 'indirect';
      const key = makeKey(cot, category);
      const val = salesPlanValues[key]?.[futureDates[fromIndex].dateString] ?? 0;
      const rowKey = `${cot.toLowerCase()}${futureDates[fromIndex].dateString}${category}`;

      setCopyRows((prev) => new Set([...prev, rowKey]));
      setSalesPlanValues((prev) => {
        const updated = { ...prev[key] };
        for (let i = fromIndex + 1; i < futureDates.length; i++) {
          updated[futureDates[i].dateString] = val;
        }
        return { ...prev, [key]: updated };
      });
    },
    [directCOT, indirectCOT, futureDates, salesPlanValues],
  );

  const handleResetButtonClick = useCallback(
    (isDirect: boolean, fromIndex: number) => {
      const cot = isDirect ? directCOT.value : indirectCOT.value;
      const category = isDirect ? 'direct' : 'indirect';
      const rowKey = `${cot.toLowerCase()}${futureDates[fromIndex].dateString}${category}`;
      setCopyRows((prev) => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
    },
    [directCOT, indirectCOT, futureDates],
  );

  // -------------------------------------------------------------------------
  // Overall Market Share
  // -------------------------------------------------------------------------

  const calculateOverallMS = (): string => {
    const row = mockSalesPlanEditData.find(
      (r) =>
        r.cot.toLowerCase() === directCOT.value.toLowerCase() &&
        r.category === 'direct' &&
        r.planningPeriod === futureDates[0].dateString,
    );
    return row ? `${row.overallMSPercent}%` : '-';
  };

  // -------------------------------------------------------------------------
  // Old/New values switch helpers
  // -------------------------------------------------------------------------

  const isOldValueCheckBoxVisible = (_cotValue: string, _isDirect: boolean): boolean => {
    // In the actual app this checks if there's an existing plan; in the mockup always show it
    return true;
  };

  const isOldValuesCheckBoxChecked = (cotValue: string, isDirect: boolean): boolean =>
    oldValuesChecked[`${cotValue}|${isDirect}`] ?? false;

  const toggleOldValuesChekedSwitch = (cotValue: string, isDirect: boolean) => {
    setOldValuesChecked((prev) => ({
      ...prev,
      [`${cotValue}|${isDirect}`]: !prev[`${cotValue}|${isDirect}`],
    }));
  };

  // -------------------------------------------------------------------------
  // Mock action handlers
  // -------------------------------------------------------------------------

  const handleSave = () => alert('Save (MOCKUP — no API call).');
  const handleFinish = () => alert('Finish (MOCKUP — no API call).');
  const handleFinishAll = () => alert('Finish All (MOCKUP — no API call).');
  const handleCancel = () => window.history.back();
  const handleShowSummary = () => alert('Show Summary (MOCKUP — no API call).');

  // -------------------------------------------------------------------------
  // Shared: Sales Summary box
  // -------------------------------------------------------------------------

  const renderSalesSummary = (cotValue: string, category: 'direct' | 'indirect') => (
    <div className={classes.saleDetails}>
      <div>
        <div
          style={{
            fontSize: '15px',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          YTD:
          <span>{getSalesSummaryValue(mockSalesSummary, cotValue, 'YTD', category)}</span>
        </div>
        <div className={classes.vertical} />
        <div
          style={{
            fontSize: '15px',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          3MA:
          <span>{getSalesSummaryValue(mockSalesSummary, cotValue, '3MA', category)}</span>
        </div>
        <div className={classes.vertical} />
        <div
          style={{
            fontSize: '15px',
            fontWeight: '600',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          PM:
          <span>{getSalesSummaryValue(mockSalesSummary, cotValue, 'PM', category)}</span>
        </div>
      </div>
    </div>
  );

  // -------------------------------------------------------------------------
  // Shared: Sales Plan editable table
  // -------------------------------------------------------------------------

  const renderSalesPlanTable = (cot: CotOption, category: 'direct' | 'indirect') => {
    const isDirect = category === 'direct';

    return (
      <div>
        <div className={classes.planTable} style={{ marginTop: '-1rem' }}>
          <table>
            <thead>
              <th>
                <td>Planning Period</td>
              </th>
              <th>
                <td>Market Forecast</td>
              </th>
              <th>
                <td>Adj Market Forecast</td>
              </th>
              <th>
                <td>TMS</td>
              </th>
              <th>
                <td>MS Plan</td>
              </th>
              <th>
                <td>EU Sales Plan</td>
              </th>
              <th style={{ width: '0.5rem' }}></th>
            </thead>
            <tbody>
              {futureDates.map((month, idx) => {
                const rowKey = `${cot.value.toLowerCase()}${month.dateString}${category}`;
                return (
                  <tr key={month.dateString}>
                    <td>{month.label}</td>
                    <td style={{ color: '#7B4B9F', fontWeight: '600' }}>
                      {getSalesplanValue(cot.value, category, month.dateString, 'marketForecast')}
                    </td>
                    <td style={{ color: '#7B4B9F', fontWeight: '600' }}>
                      <div className={classes.forecastInputContainer}>
                        <InputText
                          type="text"
                          value={getAdjMarketForecast(cot.value, category, month.dateString).toLocaleString('en-US')}
                          className={classes.forecastInp}
                          onChange={(e) =>
                            updateForecastValue(e.target.value, month.dateString, cot.value, isDirect)
                          }
                        />
                      </div>
                    </td>
                    <td style={{ fontWeight: '600' }}>
                      {getSalesplanValue(cot.value, category, month.dateString, 'tmsvalue')}
                    </td>
                    <td style={{ fontWeight: '600' }}>
                      {getSalesplanValue(cot.value, category, month.dateString, 'msplan')}
                    </td>
                    <td style={{ fontWeight: '600' }}>
                      {getSalesplanValue(cot.value, category, month.dateString, 'euforecast')}
                    </td>
                    <td>
                      <div
                        style={{
                          marginTop: '0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <Button
                          style={{ height: '28px', width: '28px' }}
                          icon={
                            <img
                              src={CopyIcon}
                              style={{
                                height: '20px',
                                width: '20px',
                                cursor: 'pointer',
                                opacity: '0.8',
                              }}
                            />
                          }
                          onClick={() => handleCopyButtonClick(isDirect, idx)}
                          rounded
                          text
                          aria-label="Filter"
                        />
                        {copyRows.has(rowKey) ? (
                          <Button
                            style={{ height: '28px', width: '28px' }}
                            icon={
                              <img
                                src={ResetIcon}
                                style={{
                                  height: '20px',
                                  width: '20px',
                                  cursor: 'pointer',
                                  opacity: '0.8',
                                }}
                              />
                            }
                            onClick={() => handleResetButtonClick(isDirect, idx)}
                            rounded
                            text
                            aria-label="Filter"
                          />
                        ) : (
                          <div></div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Shared: section wrapper (used for both Direct and Indirect accordions)
  // -------------------------------------------------------------------------

  const renderSection = (
    cot: CotOption,
    setCot: (c: CotOption) => void,
    category: 'direct' | 'indirect',
    radioName: string,
    accordionLabel: string,
    defaultOpen: boolean,
  ) => {
    const isDirect = category === 'direct';

    return (
      <div
        style={{
          marginLeft: '0.5rem',
          marginRight: '0.5rem',
          marginTop: '0.5rem',
        }}
      >
        <Accordion className={classes.accordianComponent} activeIndex={defaultOpen ? 0 : undefined}>
          <AccordionTab header={accordionLabel}>
            <div className={classes.directContainer}>
              <div
                style={{
                  margin: '0',
                  height: '4rem',
                  marginBottom: '-2rem',
                }}
              >
                <div className={classes.topHeader}>
                  {/* Sales summary box */}
                  {renderSalesSummary(cot.value, category)}

                  {/* COT radio group */}
                  <div className={classes.directRadioGrp}>
                    {[
                      { label: 'CMOP', value: 'cmop' },
                      { label: 'VA Facility', value: 'va facility' },
                      { label: 'TMOP', value: 'tmop' },
                      { label: 'MTF', value: 'mtf' },
                    ].map((opt) => (
                      <span key={opt.value}>
                        <input
                          type="radio"
                          name={radioName}
                          checked={cot.label === opt.label}
                          onClick={() => setCot(opt)}
                          onChange={() => {}}
                        />
                        <label>{opt.label}</label>
                      </span>
                    ))}
                  </div>

                  {/* New values / Old values InputSwitch */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: '8px',
                      fontSize: '14px',
                      gap: '0.5rem',
                      marginTop: isDirect ? '-2rem' : '0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                      }}
                    >
                      {isOldValueCheckBoxVisible(cot.value, isDirect) && (
                        <>
                          <span>New values</span>
                          <InputSwitch
                            style={{ marginTop: '0' }}
                            checked={isOldValuesCheckBoxChecked(cot.value, isDirect)}
                            onChange={() => toggleOldValuesChekedSwitch(cot.value, isDirect)}
                          />
                          <span>Old values</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Table */}
              {renderSalesPlanTable(cot, category)}
            </div>

            {/* Per-accordion Save / Finish buttons */}
            <div className={classes.categoryButtonGroup}>
              <Button className={classes.saveBtnStyle} onClick={handleSave}>
                Save
              </Button>
              <Button className={classes.finishBtnStyle} onClick={handleFinish}>
                Finish
              </Button>
            </div>
          </AccordionTab>
        </Accordion>
      </div>
    );
  };

  // -------------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------------

  return (
    <div className={classes.targetMarketContainer}>
      {/* Concept details card */}
      <div className={classes.planningContainer}>
        <div className={classes.tmsHeading}>
          <h2>Sales Planning</h2>
        </div>
        <ConceptDetailsSection
          conceptDetails={mockConceptDetails}
          contractDetails={mockContractDetails}
          mode="add"
        />
      </div>

      {/* Main content + Notes side panel */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        {/* Plan container */}
        <div style={{ width: '90rem', flexGrow: 1 }}>
          <div className={classes.planDetailsContainer}>
            <h4
              style={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <span>
                Plan {futureDates[0].label} - {futureDates[14].label}{' '}
                <span style={{ color: 'green' }}>*</span>
              </span>
              <span style={{ paddingRight: '3rem' }}>
                Overall Market Share: {calculateOverallMS()}
              </span>
            </h4>

            {/* Direct accordion */}
            {renderSection(
              directCOT,
              setDirectCOT,
              'direct',
              'directRadio',
              'Direct',
              true,
            )}

            <hr />

            {/* Indirect accordion */}
            {renderSection(
              indirectCOT,
              setIndirectCOT,
              'indirect',
              'indirectRadio',
              'Indirect',
              false,
            )}

            {/* Bottom action row */}
            <div className={classes.bottomBtngroup}>
              <Button
                style={{
                  backgroundColor: '#4D2E6D',
                  color: '#FFFFFF',
                  fontWeight: 600,
                  border: 'none',
                }}
                onClick={handleShowSummary}
              >
                Show Summary
              </Button>
              <div>
                <Button
                  style={{
                    backgroundColor: '#D8000C',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    border: 'none',
                  }}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  style={{
                    backgroundColor: '#4D2E6D',
                    color: '#FFFFFF',
                    fontWeight: 600,
                    border: 'none',
                  }}
                  onClick={handleFinishAll}
                >
                  Finish All
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Notes side panel */}
        <div style={{ marginTop: '0.5rem' }}>
          <NotesSection
            mode="edit"
            isNotesDataLoading={false}
            notesData={mockNotesData}
            isNoteAddLoading={false}
            isAddNewButtonDisabled={false}
            handleAddNote={() => {}}
            handleEditNote={() => {}}
            handleDeleteNote={() => {}}
            setNoteData={() => {}}
            removeNoteData={() => {}}
          />
        </div>
      </div>
    </div>
  );
};

export default SalesPlanEdit;
